// Investigations, clinical procedures, specialist consults, care plan.
//
// Two labelling rules this file exists to hold:
//
//   * An investigation result is either an UPLOADED DOCUMENT or a CLINICIAN
//     NOTE, and both are labelled as such. There is no structured lab result
//     in this system, so the UI never implies one — no reference ranges, no
//     flags derived from a value, no trend line over a result.
//
//   * Procedures shown here are the CLINICAL record. Billing lives in the
//     admission's charges panel and is never mixed in.
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, Textarea, NativeSelect } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/States';
import VoiceMicButton from '@/components/VoiceMicButton';
import VoiceFormFiller, { type VoiceField } from '@/components/VoiceFormFiller';
import { ApiError } from '@/lib/api/client';
import { mergeTextFields } from '@/lib/voiceFill';
import {
  useInvestigations, useCreateInvestigation, useReviewInvestigation,
  useProcedures, useCreateProcedure,
  useConsults, useCreateConsult, useSetConsultStatus,
  useCarePlan, useCreateCarePlanActivity, useCompleteCarePlanActivity,
} from '../hooks';
import type { CarePlanCategory, ConsultStatus } from '../types';
import { Section, Empty, fmtDateTime, useCan, BackdateField } from '../ui';

function useErr() {
  const [err, setErr] = useState<string | null>(null);
  const fail = (e: unknown) => setErr(e instanceof ApiError ? e.message : 'Something went wrong.');
  const node = err ? <div className="mb-2 rounded bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{err}</div> : null;
  return { fail, node, clear: () => setErr(null) };
}

// ── Investigations ───────────────────────────────────────────────

export function InvestigationsSection({ admissionId, readOnly }: { admissionId: number; readOnly: boolean }) {
  const can = useCan();
  const { data, isLoading } = useInvestigations(admissionId);
  const create = useCreateInvestigation(admissionId);
  const review = useReviewInvestigation(admissionId);
  const { fail, node, clear } = useErr();
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ investigation_type: 'lab', investigation_name: '', priority: 'routine', reason: '', is_critical: false });
  const [orderedAt, setOrderedAt] = useState<string | undefined>(undefined);

  const mayWrite = can('ip:investigation') && !readOnly;

  return (
    <Section
      focusId="investigation"
      title="Investigations"
      subtitle="Orders and their results. This system has no laboratory module — a result is an uploaded document or a clinician’s note, never a parsed value."
      action={mayWrite && !adding && <Button size="sm" data-focus="investigation:new" onClick={() => setAdding(true)}>+ Investigation</Button>}
    >
      {adding && (
        <div className="mb-3 flex flex-wrap items-end gap-2 rounded-lg border border-border bg-muted/40 p-3">
          <div>
            <label className="mb-1 block text-xs font-semibold">Type</label>
            <NativeSelect className="w-32" value={f.investigation_type}
              onChange={e => setF(p => ({ ...p, investigation_type: e.target.value }))}>
              {['lab', 'imaging', 'cardiac', 'pathology', 'other'].map(t => <option key={t}>{t}</option>)}
            </NativeSelect>
          </div>
          <div className="min-w-[200px] flex-1">
            <label className="mb-1 block text-xs font-semibold">Investigation</label>
            <Input value={f.investigation_name} onChange={e => setF(p => ({ ...p, investigation_name: e.target.value }))}
              placeholder="e.g. Serum creatinine" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold">Priority</label>
            <NativeSelect className="w-28" value={f.priority}
              onChange={e => setF(p => ({ ...p, priority: e.target.value }))}>
              {['routine', 'urgent', 'stat'].map(t => <option key={t}>{t}</option>)}
            </NativeSelect>
          </div>
          <div className="min-w-[160px] flex-1">
            <label className="mb-1 block text-xs font-semibold">Reason</label>
            <div className="flex items-center gap-1">
              <Input value={f.reason} onChange={e => setF(p => ({ ...p, reason: e.target.value }))} className="flex-1" />
              <VoiceMicButton onText={t => setF(p => ({ ...p, reason: (p.reason ? p.reason + ' ' : '') + t }))} title="Dictate reason" />
            </div>
          </div>
          <label className="flex items-center gap-1.5 pb-2 text-[12.5px]">
            <input type="checkbox" checked={f.is_critical}
              onChange={e => setF(p => ({ ...p, is_critical: e.target.checked }))} />
            Flag critical
          </label>
          <div className="w-full">
            <BackdateField value={orderedAt} onChange={setOrderedAt} label="Ordered at" />
          </div>
          <div className="flex gap-2 pb-0.5">
            <Button variant="ghost" size="sm" onClick={() => { setAdding(false); clear(); }}>Cancel</Button>
            <Button size="sm" loading={create.isPending} disabled={!f.investigation_name.trim()}
              onClick={() => {
                clear();
                create.mutate({ ...f, ordered_at: orderedAt } as never, {
                  onSuccess: () => { setAdding(false); setF({ investigation_type: 'lab', investigation_name: '', priority: 'routine', reason: '', is_critical: false }); setOrderedAt(undefined); },
                  onError: fail,
                });
              }}>Order</Button>
          </div>
        </div>
      )}
      {node}

      {isLoading ? <LoadingState label="Loading investigations…" />
        : !data?.length ? <Empty label="No investigations ordered" />
        : (
          <div className="space-y-2">
            {data.map(i => (
              <div key={i.id} data-focus={`investigation:${i.id}${i.is_critical && !i.reviewed_at ? ' investigation:critical' : ''}`} className="rounded-lg border border-border px-3 py-2">
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[13px] font-semibold">{i.investigation_name}</span>
                    <Badge variant="neutral">{i.investigation_type}</Badge>
                    {i.priority !== 'routine' && <Badge variant="warn">{i.priority}</Badge>}
                    {i.is_critical && (
                      <Badge variant={i.reviewed_at ? 'neutral' : 'danger'}>
                        {i.reviewed_at ? 'critical — reviewed' : 'CRITICAL — not reviewed'}
                      </Badge>
                    )}
                    <Badge variant={i.result_status === 'final' ? 'success' : 'neutral'}>
                      {i.status} · result {i.result_status}
                    </Badge>
                  </div>
                  {mayWrite && !i.reviewed_at && (
                    <Button variant="ghost" size="sm" loading={review.isPending}
                      onClick={() => review.mutate({ invId: i.id }, { onError: fail })}>
                      Mark reviewed
                    </Button>
                  )}
                </div>
                {i.reason && <div className="mt-0.5 text-[12px] text-ink-muted">For: {i.reason}</div>}

                {/* The two honest result carriers, each explicitly labelled. */}
                {i.result_summary && (
                  <div className="mt-1.5 rounded border border-border bg-muted/40 px-2.5 py-1.5">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-ink-muted">Clinician note</div>
                    <div className="text-[12.5px]">{i.result_summary}</div>
                  </div>
                )}
                {i.result_document_name && (
                  <div className="mt-1.5 rounded border border-border bg-muted/40 px-2.5 py-1.5">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-ink-muted">Uploaded document</div>
                    <div className="text-[12.5px]">{i.result_document_name}</div>
                  </div>
                )}

                <div className="mt-1.5 text-[11px] text-ink-muted">
                  Ordered {fmtDateTime(i.ordered_at)}{i.ordered_by_name ? ` by ${i.ordered_by_name}` : ''}
                  {i.reviewed_at ? ` · reviewed ${fmtDateTime(i.reviewed_at)} by ${i.reviewed_by_name ?? i.reviewed_by}` : ''}
                </div>
              </div>
            ))}
          </div>
        )}
    </Section>
  );
}

const PROCEDURE_VOICE_FIELDS: VoiceField[] = [
  { key: 'indication', label: 'Indication', type: 'text' },
  { key: 'findings', label: 'Findings', type: 'text' },
  { key: 'complications', label: 'Complications', type: 'text' },
];

const CONSULT_VOICE_FIELDS: VoiceField[] = [
  { key: 'reason', label: 'Reason', type: 'text' },
  { key: 'clinical_question', label: 'Clinical question', type: 'text' },
];

// ── Procedures ───────────────────────────────────────────────────

export function ProceduresSection({ admissionId, readOnly }: { admissionId: number; readOnly: boolean }) {
  const can = useCan();
  const { data, isLoading } = useProcedures(admissionId);
  const create = useCreateProcedure(admissionId);
  const { fail, node, clear } = useErr();
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ procedure_name: '', indication: '', performer: '', location: '', findings: '', complications: '', outcome: 'successful' });
  const [performedAt, setPerformedAt] = useState<string | undefined>(undefined);

  const mayWrite = can('ip:procedure') && !readOnly;
  const set = (k: string, v: string) => setF(p => ({ ...p, [k]: v }));

  return (
    <Section
      focusId="procedure"
      title="Clinical procedures"
      subtitle="The clinical record of what was done. Separate from billing — procedure charges live on the admission’s charges panel."
      action={mayWrite && !adding && <Button size="sm" data-focus="procedure:new" onClick={() => setAdding(true)}>+ Procedure</Button>}
    >
      {adding && (
        <div className="mb-3 grid gap-2 rounded-lg border border-border bg-muted/40 p-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-semibold">Procedure</label>
            <Input value={f.procedure_name} onChange={e => set('procedure_name', e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <VoiceFormFiller
              fields={PROCEDURE_VOICE_FIELDS}
              label="🎙 Dictate indication / findings / complications"
              onFields={extracted => setF(p => ({ ...p, ...mergeTextFields(p, extracted, ['indication', 'findings', 'complications']) }))}
            />
          </div>
          <div><label className="mb-1 block text-xs font-semibold">Indication</label>
            <Input value={f.indication} onChange={e => set('indication', e.target.value)} /></div>
          <div><label className="mb-1 block text-xs font-semibold">Performed by</label>
            <Input value={f.performer} onChange={e => set('performer', e.target.value)} /></div>
          <div><label className="mb-1 block text-xs font-semibold">Location</label>
            <Input value={f.location} onChange={e => set('location', e.target.value)} placeholder="bedside / OT" /></div>
          <div><label className="mb-1 block text-xs font-semibold">Outcome</label>
            <NativeSelect value={f.outcome} onChange={e => set('outcome', e.target.value)}>
              {['successful', 'partial', 'abandoned', 'complication'].map(o => <option key={o}>{o}</option>)}
            </NativeSelect></div>
          <div className="sm:col-span-2"><label className="mb-1 block text-xs font-semibold">Findings</label>
            <Textarea rows={2} value={f.findings} onChange={e => set('findings', e.target.value)} /></div>
          <div className="sm:col-span-2"><label className="mb-1 block text-xs font-semibold">Complications</label>
            <Input value={f.complications} onChange={e => set('complications', e.target.value)} /></div>
          <div className="sm:col-span-2">
            <BackdateField value={performedAt} onChange={setPerformedAt} label="Performed at" />
          </div>
          <div className="flex justify-end gap-2 sm:col-span-2">
            <Button variant="ghost" size="sm" onClick={() => { setAdding(false); clear(); }}>Cancel</Button>
            <Button size="sm" loading={create.isPending} disabled={!f.procedure_name.trim()}
              onClick={() => { clear(); create.mutate({ ...f, performed_datetime: performedAt } as never, { onSuccess: () => { setAdding(false); setPerformedAt(undefined); }, onError: fail }); }}>
              Record procedure
            </Button>
          </div>
        </div>
      )}
      {node}

      {isLoading ? <LoadingState label="Loading procedures…" />
        : !data?.length ? <Empty label="No clinical procedures recorded" />
        : (
          <div className="space-y-2">
            {data.map(p => (
              <div key={p.id} data-focus={`procedure:${p.id}`} className="rounded-lg border border-border px-3 py-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[13px] font-semibold">{p.procedure_name}</span>
                  {p.outcome && (
                    <Badge variant={p.outcome === 'successful' ? 'success' : p.outcome === 'complication' ? 'danger' : 'warn'}>
                      {p.outcome}
                    </Badge>
                  )}
                </div>
                {p.indication && <div className="mt-0.5 text-[12px] text-ink-muted">For: {p.indication}</div>}
                {p.findings && <div className="mt-1 text-[12.5px]">{p.findings}</div>}
                {p.complications && (
                  <div className="mt-1 text-[12.5px] text-danger-ink">Complications: {p.complications}</div>
                )}
                <div className="mt-1.5 text-[11px] text-ink-muted">
                  {fmtDateTime(p.performed_datetime)}
                  {p.performer ? ` · ${p.performer}` : ''}{p.location ? ` · ${p.location}` : ''}
                </div>
              </div>
            ))}
          </div>
        )}
    </Section>
  );
}

// ── Consults ─────────────────────────────────────────────────────

const NEXT_STATUS: Record<ConsultStatus, ConsultStatus[]> = {
  requested: ['accepted', 'cancelled'],
  accepted: ['in-progress', 'completed', 'cancelled'],
  'in-progress': ['completed', 'cancelled'],
  completed: [],
  cancelled: [],
};

export function ConsultsSection({ admissionId, readOnly }: { admissionId: number; readOnly: boolean }) {
  const can = useCan();
  const { data, isLoading } = useConsults(admissionId);
  const create = useCreateConsult(admissionId);
  const setStatus = useSetConsultStatus(admissionId);
  const { fail, node, clear } = useErr();
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ specialty_name: '', reason: '', clinical_question: '', priority: 'routine' });
  const [requestedAt, setRequestedAt] = useState<string | undefined>(undefined);

  const mayWrite = can('ip:consult') && !readOnly;
  const set = (k: string, v: string) => setF(p => ({ ...p, [k]: v }));

  return (
    <Section
      focusId="consult"
      title="Specialist consults"
      subtitle="Requested → accepted → in progress → completed."
      action={mayWrite && !adding && <Button size="sm" data-focus="consult:new" onClick={() => setAdding(true)}>+ Consult</Button>}
    >
      {adding && (
        <div className="mb-3 grid gap-2 rounded-lg border border-border bg-muted/40 p-3 sm:grid-cols-2">
          <div><label className="mb-1 block text-xs font-semibold">Specialty</label>
            <Input value={f.specialty_name} onChange={e => set('specialty_name', e.target.value)} placeholder="e.g. Nephrology" /></div>
          <div><label className="mb-1 block text-xs font-semibold">Priority</label>
            <NativeSelect value={f.priority} onChange={e => set('priority', e.target.value)}>
              {['routine', 'urgent', 'emergency'].map(p => <option key={p}>{p}</option>)}
            </NativeSelect></div>
          <div className="sm:col-span-2">
            <VoiceFormFiller
              fields={CONSULT_VOICE_FIELDS}
              label="🎙 Dictate reason / clinical question"
              onFields={extracted => setF(p => ({ ...p, ...mergeTextFields(p, extracted, ['reason', 'clinical_question']) }))}
            />
          </div>
          <div className="sm:col-span-2"><label className="mb-1 block text-xs font-semibold">Reason</label>
            <Input value={f.reason} onChange={e => set('reason', e.target.value)} /></div>
          <div className="sm:col-span-2"><label className="mb-1 block text-xs font-semibold">Clinical question</label>
            <Input value={f.clinical_question} onChange={e => set('clinical_question', e.target.value)} /></div>
          <div className="sm:col-span-2">
            <BackdateField value={requestedAt} onChange={setRequestedAt} label="Requested at" />
          </div>
          <div className="flex justify-end gap-2 sm:col-span-2">
            <Button variant="ghost" size="sm" onClick={() => { setAdding(false); clear(); }}>Cancel</Button>
            <Button size="sm" loading={create.isPending} disabled={!f.reason.trim()}
              onClick={() => { clear(); create.mutate({ ...f, requested_at: requestedAt } as never, { onSuccess: () => { setAdding(false); setRequestedAt(undefined); }, onError: fail }); }}>
              Request consult
            </Button>
          </div>
        </div>
      )}
      {node}

      {isLoading ? <LoadingState label="Loading consults…" />
        : !data?.length ? <Empty label="No consults requested" />
        : (
          <div className="space-y-2">
            {data.map(c => (
              <div key={c.id} data-focus={`consult:${c.id}`} className="rounded-lg border border-border px-3 py-2">
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[13px] font-semibold">{c.specialty_name || c.specialty_current_name || 'Specialist'}</span>
                    <Badge variant={c.status === 'completed' ? 'success' : c.status === 'cancelled' ? 'neutral' : 'info'}>
                      {c.status}
                    </Badge>
                    {c.priority !== 'routine' && <Badge variant="warn">{c.priority}</Badge>}
                  </div>
                  {mayWrite && NEXT_STATUS[c.status]?.length > 0 && (
                    <div className="flex gap-1.5">
                      {NEXT_STATUS[c.status]!.map(next => (
                        <Button key={next} variant="ghost" size="sm"
                          onClick={() => setStatus.mutate({ consultId: c.id, status: next }, { onError: fail })}>
                          {next === 'in-progress' ? 'Start' : next.charAt(0).toUpperCase() + next.slice(1)}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
                {c.reason && <div className="mt-0.5 text-[12.5px]">{c.reason}</div>}
                {c.recommendations && (
                  <div className="mt-1.5 rounded border border-border bg-muted/40 px-2.5 py-1.5 text-[12.5px]">
                    <span className="text-ink-muted">Recommendations: </span>{c.recommendations}
                  </div>
                )}
                <div className="mt-1.5 text-[11px] text-ink-muted">
                  Requested {fmtDateTime(c.requested_at)}{c.requested_by_name ? ` by ${c.requested_by_name}` : ''}
                  {c.completed_at ? ` · completed ${fmtDateTime(c.completed_at)}` : ''}
                </div>
              </div>
            ))}
          </div>
        )}
    </Section>
  );
}

// ── Care plan ────────────────────────────────────────────────────

const CARE_CATEGORIES: CarePlanCategory[] = [
  'monitoring', 'medication', 'nutrition', 'mobility', 'wound-care',
  'oxygen', 'physiotherapy', 'education', 'discharge-preparation', 'other',
];

export function CarePlanSection({ admissionId, readOnly }: { admissionId: number; readOnly: boolean }) {
  const can = useCan();
  const { data, isLoading } = useCarePlan(admissionId);
  const create = useCreateCarePlanActivity(admissionId);
  const complete = useCompleteCarePlanActivity(admissionId);
  const { fail, node, clear } = useErr();
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState<{ category: CarePlanCategory; description: string; owner_role: string }>({
    category: 'monitoring', description: '', owner_role: '',
  });

  const mayWrite = can('ip:care-plan') && !readOnly;

  return (
    <Section
      focusId="care_plan"
      title="Care plan"
      subtitle="Active goals and tasks for this admission."
      action={mayWrite && !adding && <Button size="sm" data-focus="care_plan:new" onClick={() => setAdding(true)}>+ Activity</Button>}
    >
      {adding && (
        <div className="mb-3 flex flex-wrap items-end gap-2 rounded-lg border border-border bg-muted/40 p-3">
          <div>
            <label className="mb-1 block text-xs font-semibold">Category</label>
            <NativeSelect className="w-44" value={f.category}
              onChange={e => setF(p => ({ ...p, category: e.target.value as CarePlanCategory }))}>
              {CARE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </NativeSelect>
          </div>
          <div className="min-w-[220px] flex-1">
            <label className="mb-1 block text-xs font-semibold">Activity</label>
            <div className="flex items-center gap-1">
              <Input value={f.description} onChange={e => setF(p => ({ ...p, description: e.target.value }))} className="flex-1" />
              <VoiceMicButton onText={t => setF(p => ({ ...p, description: (p.description ? p.description + ' ' : '') + t }))} title="Dictate activity" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold">Owner</label>
            <Input className="w-32" value={f.owner_role}
              onChange={e => setF(p => ({ ...p, owner_role: e.target.value }))} placeholder="Nurse / Doctor" />
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setAdding(false); clear(); }}>Cancel</Button>
            <Button size="sm" loading={create.isPending} disabled={!f.description.trim()}
              onClick={() => {
                clear();
                create.mutate(f, {
                  onSuccess: () => { setAdding(false); setF({ category: 'monitoring', description: '', owner_role: '' }); },
                  onError: fail,
                });
              }}>Add</Button>
          </div>
        </div>
      )}
      {node}

      {isLoading ? <LoadingState label="Loading care plan…" />
        : !data?.length ? <Empty label="No care-plan activities" />
        : (
          <ul className="space-y-1.5">
            {data.map(a => (
              <li key={a.id} data-focus={`care_plan:${a.id}`} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-lg border border-border px-3 py-2">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <Badge variant="neutral">{a.category}</Badge>
                  <span className={`text-[12.5px] ${a.status === 'completed' ? 'text-ink-muted line-through' : ''}`}>
                    {a.description}
                  </span>
                  {a.owner_role && <span className="text-[11px] text-ink-muted">({a.owner_role})</span>}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={a.status === 'completed' ? 'success' : a.status === 'on-hold' ? 'warn' : 'info'}>
                    {a.status}
                  </Badge>
                  {mayWrite && a.status !== 'completed' && a.status !== 'cancelled' && (
                    <Button variant="ghost" size="sm"
                      onClick={() => complete.mutate({ activityId: a.id }, { onError: fail })}>
                      Complete
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
    </Section>
  );
}
